import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Clock3, ExternalLink, MessageSquareWarning, MoreHorizontal, UserRound } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { DocumentModal } from "@/pages/documents/DocumentModal";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Label, TextArea } from "@/components/ui/Input";
import { HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { computeHorizontalMenuPanel, FLOATING_POPOVER_Z_INDEX, type AnchoredMenuPanelBox } from "@/lib/viewportAnchoredPopover";
import { formatTimestamp } from "@/lib/utils";

import {
  deleteInsideSalesApproval,
  fetchInsideSalesApprovals,
  fetchApprovalLocationOptions,
  INSIDE_SALES_APPROVALS_QUERY_KEY,
  transitionInsideSalesApproval,
  type ApprovalColumn,
  type ApprovalQueueItem,
  type ApprovalTransitionAction,
} from "./insideSalesApprovalsApi";
import { InsideSalesApprovalTicketPanel, kindLabel } from "./InsideSalesApprovalTicketPanel";

const COLUMNS: Array<{ key: Exclude<ApprovalColumn, "done">; label: string }> = [
  { key: "processing", label: "Processing" },
  { key: "make_changes", label: "Make changes" },
  { key: "preapproval", label: "Pre-approval" },
  { key: "waiting_for_approval", label: "Waiting for approval" },
  { key: "approved", label: "Approved" },
];

function ageLabel(createdAt: string): string {
  const elapsed = Math.max(0, Date.now() - new Date(createdAt).getTime());
  const hours = Math.floor(elapsed / 3_600_000);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function mutationErrorMessage(error: unknown): string {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return "You do not have permission to perform this approval action.";
  }
  return error instanceof Error ? error.message : "The approval could not be updated.";
}

function ApprovalTicketOverflowMenu({
  disabled,
  onDelete,
}: {
  disabled: boolean;
  onDelete: () => void;
}) {
  const overflowMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [overflowMenuBox, setOverflowMenuBox] = useState<AnchoredMenuPanelBox | null>(null);

  const updateOverflowMenuPosition = useCallback(() => {
    const trigger = overflowMenuButtonRef.current;
    if (trigger == null) {
      setOverflowMenuBox(null);
      return;
    }
    setOverflowMenuBox(
      computeHorizontalMenuPanel({
        anchorRect: trigger.getBoundingClientRect(),
        menuWidth: 180,
        horizontalAlign: "end",
        maxHeightCap: 160,
      }),
    );
  }, []);

  useEffect(() => {
    if (!overflowMenuOpen) return;
    updateOverflowMenuPosition();
    const onScrollOrResize = () => updateOverflowMenuPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [overflowMenuOpen, updateOverflowMenuPosition]);

  useEffect(() => {
    if (!overflowMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (overflowMenuButtonRef.current?.contains(target)) return;
      setOverflowMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [overflowMenuOpen]);

  return (
    <>
      <Button
        ref={overflowMenuButtonRef}
        type="button"
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0 text-slate-500 hover:text-slate-900"
        aria-label="More ticket actions"
        aria-haspopup="menu"
        aria-expanded={overflowMenuOpen}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setOverflowMenuOpen((open) => !open);
        }}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </Button>
      {overflowMenuOpen && overflowMenuBox != null
        ? createPortal(
            <div
              className="fixed overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={{
                left: overflowMenuBox.left,
                top: overflowMenuBox.top,
                width: overflowMenuBox.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-slate-50"
                disabled={disabled}
                onClick={() => {
                  setOverflowMenuOpen(false);
                  onDelete();
                }}
              >
                Delete ticket
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function ApprovalCard({
  approval,
  actionsDisabled,
  onApprove,
  onRequestChanges,
  onDelete,
  onOpen,
  selected,
}: {
  approval: ApprovalQueueItem;
  actionsDisabled: boolean;
  onApprove: () => void;
  onRequestChanges: () => void;
  onDelete: () => void;
  onOpen: () => void;
  selected: boolean;
}) {
  const linkedPath =
    approval.linkedEntityId == null
      ? null
      : approval.kind === "quote"
        ? `/inside-sales/quotes/${encodeURIComponent(approval.linkedEntityId)}`
        : `/inside-sales/sales-orders/${encodeURIComponent(approval.linkedEntityId)}`;

  return (
    <article className={selected ? "rounded-lg border border-brand-300 bg-white p-3 shadow-sm ring-1 ring-brand-200" : "rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:border-slate-300"}>
      <div className="flex items-start justify-between gap-2">
        <button type="button" className="min-w-0 flex-1 text-left" onClick={onOpen}>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
            {kindLabel(approval.kind)}
          </span>
          <div className="mt-2 text-sm font-semibold text-slate-900">
            {approval.name || approval.sourceTicketNumber || `Ticket ${approval.id}`}
          </div>
          <span className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
            <Clock3 className="h-3.5 w-3.5" aria-hidden />
            {ageLabel(approval.createdAt)}
          </span>
        </button>
        <div className="flex shrink-0 items-center">
          <HoverHintTrigger
            align="end"
            closeOnTriggerActivate
            trigger={
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                aria-label="Approve"
                disabled={actionsDisabled}
                onClick={(event) => {
                  event.stopPropagation();
                  onApprove();
                }}
              >
                <Check className="h-4 w-4" aria-hidden="true" />
              </Button>
            }
          >
            Approve
          </HoverHintTrigger>
          <HoverHintTrigger
            align="end"
            closeOnTriggerActivate
            trigger={
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                aria-label="Request changes"
                disabled={actionsDisabled}
                onClick={(event) => {
                  event.stopPropagation();
                  onRequestChanges();
                }}
              >
                <MessageSquareWarning className="h-4 w-4" aria-hidden="true" />
              </Button>
            }
          >
            Request changes
          </HoverHintTrigger>
          <ApprovalTicketOverflowMenu disabled={actionsDisabled} onDelete={onDelete} />
        </div>
      </div>

      <button type="button" className="mt-3 block w-full space-y-1 text-left" onClick={onOpen}>
        <div className="truncate text-sm font-medium text-slate-800">
          {approval.endUserName || approval.clientName || "Client not identified"}
        </div>
        {approval.endUserName && approval.clientName ? (
          <div className="truncate text-xs text-slate-500">{approval.clientName}</div>
        ) : null}
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <UserRound className="h-3.5 w-3.5" aria-hidden />
          {approval.assigneeDisplayName || "Unassigned"}
        </div>
      </button>

      <div className="mt-3 flex flex-wrap gap-1.5 border-t border-slate-100 pt-2">
        {approval.sourceTicketUrl ? (
          <a
            href={approval.sourceTicketUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800"
          >
            Source ticket <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
        ) : null}
        {linkedPath ? (
          <Link to={linkedPath} className="text-xs font-medium text-brand-700 hover:text-brand-800">
            {approval.linkedEntityNumber || `Linked ${approval.kind}`}
          </Link>
        ) : (
          <span className="text-xs text-slate-400">Not linked</span>
        )}
      </div>
    </article>
  );
}

export function InsideSalesApprovalsPage() {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTicketId = searchParams.get("ticket");
  const [pendingAction, setPendingAction] = useState<{
    approval: ApprovalQueueItem;
    action: Extract<ApprovalTransitionAction, "approve" | "request_changes">;
    noteRequired: boolean;
  } | null>(null);
  const [ticketToDelete, setTicketToDelete] = useState<ApprovalQueueItem | null>(null);
  const [note, setNote] = useState("");
  const [locationId, setLocationId] = useState("");
  const approvalsQuery = useQuery({
    queryKey: INSIDE_SALES_APPROVALS_QUERY_KEY,
    queryFn: fetchInsideSalesApprovals,
  });
  const locationsQuery = useQuery({
    queryKey: ["sales-orders", "locations"],
    queryFn: fetchApprovalLocationOptions,
    enabled: pendingAction?.action === "approve" && pendingAction.approval.kind === "order",
  });
  const transitionMutation = useMutation({
    mutationFn: ({
      approvalId,
      action,
      transitionNote,
      transitionLocationId,
    }: {
      approvalId: string;
      action: ApprovalTransitionAction;
      transitionNote?: string;
      transitionLocationId?: string;
    }) => transitionInsideSalesApproval(approvalId, {
      action,
      note: transitionNote,
      locationId: transitionLocationId,
    }),
    onSuccess: async () => {
      setPendingAction(null);
      setNote("");
      setLocationId("");
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_APPROVALS_QUERY_KEY });
      showToast({ message: "Approval updated.", tone: "success" });
    },
    onError: (error) => showToast({ message: mutationErrorMessage(error), tone: "error" }),
  });
  const deleteMutation = useMutation({
    mutationFn: (approvalId: string) => deleteInsideSalesApproval(approvalId),
    onSuccess: async (_void, approvalId) => {
      setTicketToDelete(null);
      if (selectedTicketId === approvalId) {
        const next = new URLSearchParams(searchParams);
        next.delete("ticket");
        setSearchParams(next, { replace: true });
      }
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_APPROVALS_QUERY_KEY });
      showToast({ message: "Ticket deleted.", tone: "success" });
    },
    onError: (error) => showToast({ message: mutationErrorMessage(error), tone: "error" }),
  });
  const visibleApprovals = useMemo(
    () => (approvalsQuery.data?.approvals ?? []).filter((approval) => approval.column !== "done"),
    [approvalsQuery.data],
  );

  function openApprove(approval: ApprovalQueueItem) {
    if (approval.kind === "order") {
      setPendingAction({ approval, action: "approve", noteRequired: false });
      setNote("");
      setLocationId("");
      return;
    }
    transitionMutation.mutate({ approvalId: approval.id, action: "approve" });
  }

  function openRequestChanges(approval: ApprovalQueueItem) {
    setPendingAction({ approval, action: "request_changes", noteRequired: true });
    setNote("");
    setLocationId("");
  }

  function openTicket(approvalId: string) {
    const next = new URLSearchParams(searchParams);
    next.set("ticket", approvalId);
    setSearchParams(next);
  }

  function closeTicket() {
    const next = new URLSearchParams(searchParams);
    next.delete("ticket");
    setSearchParams(next);
  }

  const actionsBusy = transitionMutation.isPending || deleteMutation.isPending;

  return (
      <div className="space-y-4">
        <PageTitleWithHelp hoverHint="Orders and quotes remain here until the original doer marks the approved request as sent.">
          Approvals
        </PageTitleWithHelp>

      {approvalsQuery.isLoading ? (
        <Card><CardBody className="py-6 text-sm text-slate-600">Loading approvals...</CardBody></Card>
      ) : null}
      {approvalsQuery.isError ? (
        <Card>
          <CardBody className="py-6 text-sm text-rose-700">
            {approvalsQuery.error instanceof Error ? approvalsQuery.error.message : "Could not load approvals."}
          </CardBody>
        </Card>
      ) : null}

      <div className="grid min-h-[28rem] gap-3 xl:grid-cols-5">
        {COLUMNS.map((column) => {
          const approvals = visibleApprovals.filter((approval) => approval.column === column.key);
          return (
            <section key={column.key} className="min-w-0 rounded-lg border border-slate-200 bg-slate-50/70">
              <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5">
                <h2 className="text-sm font-semibold text-slate-900">{column.label}</h2>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-600">{approvals.length}</span>
              </div>
              <div className="space-y-2.5 p-2.5">
                {approvals.length === 0 ? (
                  <div className="rounded-md border border-dashed border-slate-300 px-3 py-6 text-center text-xs text-slate-500">
                    No approvals
                  </div>
                ) : approvals.map((approval) => (
                  <ApprovalCard
                    key={approval.id}
                    approval={approval}
                    selected={selectedTicketId === approval.id}
                    actionsDisabled={actionsBusy}
                    onApprove={() => openApprove(approval)}
                    onRequestChanges={() => openRequestChanges(approval)}
                    onDelete={() => setTicketToDelete(approval)}
                    onOpen={() => openTicket(approval.id)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {pendingAction ? (
        <DocumentModal
          title={pendingAction.action === "request_changes" ? "Request changes" : "Approve sales order"}
          onClose={() => setPendingAction(null)}
          shellClassName="max-w-lg"
        >
          <div className="space-y-4">
            {pendingAction.noteRequired ? <div>
              <Label htmlFor="approval-change-note">Change note</Label>
              <TextArea
                id="approval-change-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Describe the changes needed"
                className="mt-1 min-h-28"
              />
            </div> : null}
            {pendingAction.action === "approve" && pendingAction.approval.kind === "order" ? (
              <div>
                <Label htmlFor="approval-location-id">Manufactured location id</Label>
                <select
                  id="approval-location-id"
                  value={locationId}
                  onChange={(event) => setLocationId(event.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
                >
                  <option value="">Select location</option>
                  {(locationsQuery.data?.locations ?? []).map((location) => (
                    <option key={location.id} value={String(location.id)}>
                      {[location.abbreviation, location.name].filter(Boolean).join(" - ") || location.id}
                    </option>
                  ))}
                </select>
                {locationsQuery.isError ? (
                  <div className="mt-1 text-sm text-rose-700">{locationsQuery.error.message}</div>
                ) : null}
              </div>
            ) : null}
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
              <Button type="button" variant="secondary" onClick={() => setPendingAction(null)}>Cancel</Button>
              <Button
                type="button"
                disabled={
                  (pendingAction.noteRequired && note.trim() === "") ||
                  (pendingAction.action === "approve" && pendingAction.approval.kind === "order" && locationId.trim() === "") ||
                  transitionMutation.isPending
                }
                isLoading={transitionMutation.isPending}
                onClick={() => transitionMutation.mutate({
                  approvalId: pendingAction.approval.id,
                  action: pendingAction.action,
                  transitionNote: note.trim() || undefined,
                  transitionLocationId: locationId.trim() || undefined,
                })}
              >
                {pendingAction.action === "request_changes" ? "Request changes" : "Approve"}
              </Button>
            </div>
          </div>
        </DocumentModal>
      ) : null}

      {ticketToDelete ? (
        <DocumentModal
          title="Delete ticket"
          onClose={() => setTicketToDelete(null)}
          shellClassName="max-w-lg"
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Delete {ticketToDelete.sourceTicketNumber || "this approval ticket"}? This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
              <Button type="button" variant="secondary" onClick={() => setTicketToDelete(null)}>Cancel</Button>
              <Button
                type="button"
                variant="danger"
                disabled={deleteMutation.isPending}
                isLoading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(ticketToDelete.id)}
              >
                Delete ticket
              </Button>
            </div>
          </div>
        </DocumentModal>
      ) : null}

      {selectedTicketId ? (
        <InsideSalesApprovalTicketPanel approvalId={selectedTicketId} onClose={closeTicket} />
      ) : null}

      {approvalsQuery.data ? (
        <div className="text-xs text-slate-500">Updated {formatTimestamp(new Date().toISOString())}</div>
      ) : null}
    </div>
  );
}
